/**
 * SemesterPlanPage — REDESIGNED (Feb 2026 UX pass).
 *
 * Layout reorganised around three concrete user questions:
 *   1. "How much have I done?"   → progress ring + week-pill rail at the top
 *   2. "What's on today?"         → calendar-grid for the active week
 *   3. "Where do I go next?"      → inline next-week peek button
 *
 * Previously the page was a flat list of weeks → unreadable on mobile and
 * easy to lose track of progress. The new view keeps everything visible
 * at a glance with calm whitespace and the design-token palette so dark
 * mode renders correctly.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  CalendarDays, FileUp, Loader2, Sparkles, Check, Plus, Trash2, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import EmptyState from '../components/EmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const KIND_PILL = {
  reading:    'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
  practice:   'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20',
  flashcards: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
  review:     'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
  exam_prep:  'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20',
};

// File picker accept list — now matches the backend extractor.
const ACCEPT = '.pdf,.txt,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.webp';

/** Tiny SVG progress ring. Pure CSS would be uglier on dark mode. */
const ProgressRing = ({ value, size = 76, stroke = 6 }) => {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - Math.max(0, Math.min(1, value)));
  return (
    <svg width={size} height={size} className="rotate-[-90deg]" aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="var(--isp-glass-border)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        stroke="#002FA7" strokeWidth={stroke} fill="none"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 600ms ease' }}
      />
    </svg>
  );
};

const SemesterPlanPage = () => {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [fileObj, setFileObj] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Allow the user to peek at a non-current week without bumping current_week.
  const [viewWeekIdx, setViewWeekIdx] = useState(null);
  // Tracks the plan id we just kicked off so we can surface a toast when
  // its async generation finishes (or fails).
  const pendingPlanRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['semester-plan', 'list'],
    queryFn: async () => (await axios.get(`${API}/semester-plan`)).data,
    staleTime: 60_000,
    // Auto-poll while any plan is still generating. Once they all settle
    // (status === 'ready' or 'failed'), TanStack stops refetching.
    refetchInterval: (q) => {
      const pls = q?.state?.data?.plans || [];
      return pls.some((p) => p.status === 'generating') ? 3000 : false;
    },
  });
  const plans = data?.plans || [];
  const active = plans.find((p) => p.id === activeId) || plans[0] || null;

  const toggleTask = useMutation({
    mutationFn: ({ planId, taskId, done }) =>
      axios.post(`${API}/semester-plan/${planId}/tasks/${taskId}/done?done=${done}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semester-plan'] });
    },
  });

  const advanceWeek = useMutation({
    mutationFn: (planId) => axios.post(`${API}/semester-plan/${planId}/advance-week`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['semester-plan', 'list'] }),
  });

  const deletePlan = useMutation({
    mutationFn: (planId) => axios.delete(`${API}/semester-plan/${planId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semester-plan'] });
      setActiveId(null);
      toast.success('Plan deleted.');
    },
  });

  const handleGenerate = async () => {
    if (!fileObj && pastedText.trim().length < 50) {
      toast.error('Pick a file or paste at least a few lines of your syllabus.');
      return;
    }
    setGenerating(true);
    try {
      let res;
      if (fileObj) {
        const fd = new FormData();
        fd.append('file', fileObj);
        res = await axios.post(`${API}/semester-plan/generate`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60_000,
        });
      } else {
        res = await axios.post(`${API}/semester-plan/generate-from-text`, { text: pastedText });
      }
      // Backend now returns a pending row immediately. The actual LLM call
      // runs server-side and the row flips to status='ready' when done.
      pendingPlanRef.current = res.data.id;
      toast.info('Your plan is being built — this takes about a minute.', { duration: 6000 });
      setUploadOpen(false);
      setPastedText('');
      setFileObj(null);
      setActiveId(res.data.id);
      qc.invalidateQueries({ queryKey: ['semester-plan'] });
    } catch (err) {
      if (err?.response?.status !== 402) {
        toast.error(err?.response?.data?.detail || 'Could not start plan generation.');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Watch for the just-created plan to finish generating server-side.
  useEffect(() => {
    if (!pendingPlanRef.current) return;
    const target = plans.find((p) => p.id === pendingPlanRef.current);
    if (!target) return;
    if (target.status === 'ready') {
      toast.success(`"${target.subject_name}" is ready!`);
      pendingPlanRef.current = null;
    } else if (target.status === 'failed') {
      toast.error(target.error || 'Plan generation failed. Please try again.');
      pendingPlanRef.current = null;
    }
  }, [plans]);

  // ── Derived progress ────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!active) return null;
    const allTasks = (active.weeks || []).flatMap((w) => w.tasks || []);
    const done = allTasks.filter((t) => t.done).length;
    return {
      total: allTasks.length,
      done,
      pct: allTasks.length === 0 ? 0 : done / allTasks.length,
    };
  }, [active]);

  const currentWeekIdx = active ? Math.max(0, (active.current_week || 1) - 1) : 0;
  const shownWeekIdx = viewWeekIdx ?? currentWeekIdx;
  const shownWeek = active?.weeks?.[shownWeekIdx] || null;
  const tasksByDay = useMemo(() => {
    const grouped = Object.fromEntries(DAYS.map((d) => [d, []]));
    (shownWeek?.tasks || []).forEach((t) => {
      const day = DAYS.includes(t.day) ? t.day : 'Mon';
      grouped[day].push(t);
    });
    return grouped;
  }, [shownWeek]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
      {/* ── Page header ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#002FA7] mb-1.5">
            Semester Plan
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] tracking-tight">
            Your AI-generated study plan
          </h1>
          <p className="text-sm text-[var(--isp-body)] mt-1.5 max-w-xl">
            Upload a syllabus, timetable, or course outline. Our AI lays out a week-by-week plan you simply follow.
          </p>
        </div>

        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button className="isp-btn-primary gap-2" data-testid="new-plan-btn">
              <Plus className="w-4 h-4" /> New Plan
            </Button>
          </DialogTrigger>
          <DialogContent
            className="sm:max-w-lg bg-[var(--isp-glass)] border-[var(--isp-glass-border)]"
            data-testid="upload-plan-dialog"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl text-[var(--isp-heading)]">
                <Sparkles className="w-5 h-5 text-[#002FA7]" />
                Build a study plan
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[var(--isp-muted)] mb-1.5 block">
                  Upload syllabus / timetable
                </label>
                <input
                  type="file"
                  accept={ACCEPT}
                  onChange={(e) => setFileObj(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-[var(--isp-body)] file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#002FA7]/10 file:text-[#002FA7] hover:file:bg-[#002FA7]/15"
                  data-testid="plan-file-input"
                />
                <p className="mt-1.5 text-[11px] text-[var(--isp-muted)]">
                  PDF · DOCX · XLSX · PPTX · TXT · or a phone photo of a printed timetable
                </p>
                {fileObj && <p className="mt-1.5 text-xs text-[var(--isp-heading)]">Selected: {fileObj.name}</p>}
              </div>

              <div className="text-center text-xs text-[var(--isp-muted)] before:inline-block before:w-12 before:h-px before:bg-[var(--isp-glass-border)] before:mr-2 before:align-middle after:inline-block after:w-12 after:h-px after:bg-[var(--isp-glass-border)] after:ml-2 after:align-middle">
                or paste text
              </div>

              <Textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                rows={6}
                placeholder="Paste your weekly timetable or a course outline here…"
                className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder:text-[var(--isp-muted)] font-mono text-sm"
                data-testid="plan-paste-text"
              />

              <Button
                onClick={handleGenerate}
                disabled={generating || (!fileObj && pastedText.trim().length < 50)}
                className="w-full isp-btn-primary gap-2"
                data-testid="generate-plan-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? 'Building your plan…' : 'Build my plan'}
              </Button>
              <p className="text-[11px] text-[var(--isp-muted)] text-center">
                We&apos;ll never store the uploaded file after extraction — only the generated plan is saved.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── Empty state ─────────────────────────────────────────── */}
      {!isLoading && plans.length === 0 && (
        <EmptyState
          icon={CalendarDays}
          title="No plans yet"
          description="Upload your syllabus or paste a timetable and we'll lay out the whole semester for you."
          actionLabel="Build my first plan"
          onAction={() => setUploadOpen(true)}
        />
      )}

      {/* ── Plan picker (if user has more than one) ────────────── */}
      {plans.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="plan-list">
          {plans.map((p) => {
            const isGen = p.status === 'generating';
            const isFail = p.status === 'failed';
            return (
              <button
                key={p.id}
                onClick={() => { setActiveId(p.id); setViewWeekIdx(null); }}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
                  active?.id === p.id
                    ? 'bg-[#002FA7] text-white border-[#002FA7]'
                    : 'bg-[var(--isp-glass-inner)] text-[var(--isp-body)] border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner-hover)]'
                }`}
              >
                {isGen && <Loader2 className="w-3 h-3 animate-spin" />}
                {isFail && <span aria-hidden className="text-rose-500">!</span>}
                {p.subject_name}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Generating / failed state for the active plan ──────── */}
      {active && active.status === 'generating' && (
        <div
          className="isp-card p-8 flex flex-col items-center text-center gap-3"
          data-testid="plan-generating"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" aria-hidden />
          <h2 className="text-lg font-semibold text-[var(--isp-heading)]">Building your study plan…</h2>
          <p className="text-sm text-[var(--isp-body)] max-w-md">
            Our AI is reading your syllabus and laying out the semester week-by-week. This usually takes 30–90 seconds.
          </p>
        </div>
      )}
      {active && active.status === 'failed' && (
        <div
          className="isp-card p-6 border-rose-500/30 bg-rose-500/5"
          data-testid="plan-failed"
          role="alert"
        >
          <h2 className="text-sm font-semibold text-rose-700 dark:text-rose-300">Plan generation failed</h2>
          <p className="text-sm text-[var(--isp-body)] mt-1">{active.error || 'Something went wrong. Please try again.'}</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => deletePlan.mutate(active.id)} className="text-xs">
              Dismiss
            </Button>
            <Button size="sm" onClick={() => setUploadOpen(true)} className="isp-btn-primary text-xs gap-1.5">
              <Plus className="w-3 h-3" /> Try again
            </Button>
          </div>
        </div>
      )}

      {/* ── Active plan ──────────────────────────────────────────── */}
      {active && active.status === 'ready' && stats && (
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="space-y-5"
          data-testid="active-plan"
        >
          {/* Progress hero */}
          <div className="isp-card p-5 sm:p-6 flex flex-wrap items-center gap-5">
            <div className="relative shrink-0">
              <ProgressRing value={stats.pct} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-[var(--isp-heading)] leading-none">{Math.round(stats.pct * 100)}%</span>
                <span className="text-[10px] uppercase tracking-wider text-[var(--isp-muted)] mt-0.5">done</span>
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <h2 className="text-xl font-bold text-[var(--isp-heading)] tracking-tight">{active.subject_name}</h2>
              <p className="text-xs text-[var(--isp-muted)] mt-1">
                Week {active.current_week} of {active.term_weeks} · {stats.done}/{stats.total} tasks complete
              </p>
            </div>
            <div className="flex gap-2 ml-auto">
              {active.current_week < active.term_weeks && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => advanceWeek.mutate(active.id)}
                  className="gap-1.5 text-xs"
                  data-testid="advance-week-btn"
                >
                  Next week <ChevronRight className="w-3 h-3" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { if (window.confirm('Delete this plan?')) deletePlan.mutate(active.id); }}
                className="text-[var(--isp-muted)] hover:text-rose-600"
                data-testid="delete-plan-btn"
                aria-label="Delete plan"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Week-pill rail */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="week-pills">
            {(active.weeks || []).map((w, i) => {
              const wDone = (w.tasks || []).filter((t) => t.done).length;
              const wTotal = (w.tasks || []).length;
              const isShown = i === shownWeekIdx;
              const isCurrent = i === currentWeekIdx;
              return (
                <button
                  key={w.week_number}
                  onClick={() => setViewWeekIdx(i)}
                  className={`shrink-0 min-w-[78px] px-3 py-2 rounded-xl border transition-colors text-left ${
                    isShown
                      ? 'bg-[#002FA7] text-white border-[#002FA7]'
                      : 'bg-[var(--isp-glass-inner)] text-[var(--isp-body)] border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner-hover)]'
                  }`}
                  data-testid={`week-pill-${w.week_number}`}
                >
                  <p className={`text-[10px] uppercase tracking-wider font-semibold ${isShown ? 'text-white/70' : 'text-[var(--isp-muted)]'}`}>
                    Wk {w.week_number}{isCurrent && ' • now'}
                  </p>
                  <p className={`text-xs font-bold mt-0.5 truncate ${isShown ? 'text-white' : 'text-[var(--isp-heading)]'}`}>
                    {wDone}/{wTotal}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Week detail — header + per-day grid */}
          {shownWeek && (
            <div className="isp-card p-5 sm:p-6 space-y-5">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#002FA7] mb-1">
                  Week {shownWeek.week_number} focus
                </p>
                <h3 className="text-lg font-semibold text-[var(--isp-heading)] leading-snug">
                  {shownWeek.focus}
                </h3>
                {shownWeek.key_topics?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {shownWeek.key_topics.map((kt, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-[var(--isp-glass-inner)] text-[10px] text-[var(--isp-body)] border border-[var(--isp-glass-border)]">
                        {kt}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Per-day calendar grid */}
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}
                data-testid="week-day-grid"
              >
                {DAYS.map((day) => {
                  const dayTasks = tasksByDay[day] || [];
                  if (dayTasks.length === 0) return null;
                  return (
                    <div
                      key={day}
                      className="rounded-xl border border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] p-3"
                      data-testid={`day-card-${day}`}
                    >
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--isp-muted)] mb-2">{day}</p>
                      <ul className="space-y-2">
                        {dayTasks.map((t) => (
                          <li key={t.id} className="flex items-start gap-2" data-testid={`task-${t.id}`}>
                            <button
                              onClick={() => toggleTask.mutate({ planId: active.id, taskId: t.id, done: !t.done })}
                              className={`mt-0.5 w-4 h-4 rounded-md border flex items-center justify-center transition-colors shrink-0 ${
                                t.done
                                  ? 'bg-emerald-500 border-emerald-500 text-white'
                                  : 'border-[var(--isp-glass-border)] hover:border-[#002FA7]'
                              }`}
                              aria-label="Toggle task done"
                              data-testid={`task-toggle-${t.id}`}
                            >
                              {t.done && <Check className="w-2.5 h-2.5" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs leading-snug ${t.done ? 'line-through text-[var(--isp-muted)]' : 'text-[var(--isp-heading)]'}`}>
                                {t.title}
                              </p>
                              <div className="flex items-center gap-1.5 mt-1">
                                <span className="text-[9px] text-[var(--isp-muted)]">{t.minutes}m</span>
                                <span className={`text-[9px] px-1 py-0 rounded border ${KIND_PILL[t.kind] || ''}`}>
                                  {t.kind?.replace('_', ' ')}
                                </span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {isLoading && (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--isp-muted)]" />
        </div>
      )}
    </div>
  );
};

export default SemesterPlanPage;
